import { useState, useRef } from 'react';
import { Camera, Upload, X, Image as ImageIcon, AlertCircle, CheckCircle } from 'lucide-react';
import { usePhotoUpload } from '../../hooks/usePhotoUpload';

interface PhotoUploadWidgetProps {
  onUploadComplete?: (urls: string[]) => void;
  onUploadError?: (error: string) => void;
  maxPhotos?: number;
  showPreview?: boolean;
  className?: string;
}

const PhotoUploadWidget = ({ 
  onUploadComplete,
  onUploadError,
  maxPhotos = 5,
  showPreview = true,
  className = ''
}: PhotoUploadWidgetProps) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const { uploadPhotos, isUploading, uploadProgress, error, resetUpload } = usePhotoUpload({
    maxPhotos,
    maxSizePerPhoto: 10,
    compressImages: true,
    generateThumbnails: true
  });

  const handleFiles = (files: FileList) => {
    const fileArray = Array.from(files);
    
    if (selectedFiles.length + fileArray.length > maxPhotos) {
      onUploadError?.(`Máximo ${maxPhotos} fotos permitidas`);
      return;
    }

    const validFiles: File[] = [];
    const newPreviews: string[] = [];

    fileArray.forEach(file => {
      if (file.type.startsWith('image/')) {
        validFiles.push(file);
        newPreviews.push(URL.createObjectURL(file));
      }
    });

    setSelectedFiles(prev => [...prev, ...validFiles]);
    setPreviews(prev => [...prev, ...newPreviews]);
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleFiles(e.target.files);
    }
  };

  const removeFile = (index: number) => {
    URL.revokeObjectURL(previews[index]);
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;

    try {
      const urls = await uploadPhotos(selectedFiles, {
        category: 'personal',
        tags: [],
        visibility: 'public'
      });
      
      onUploadComplete?.(urls);
      
      // Clear selections
      setSelectedFiles([]);
      setPreviews([]);
      resetUpload();
      
    } catch (err) {
      onUploadError?.(err instanceof Error ? err.message : 'Error al subir fotos');
    }
  };

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Upload Area */}
      <div
        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors duration-200 cursor-pointer ${
          dragActive
            ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
            : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={openFileDialog}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*"
          onChange={handleFileInput}
          className="hidden"
          disabled={isUploading}
        />

        <div className="space-y-3">
          <div className="flex justify-center">
            <div className="p-3 bg-gray-100 dark:bg-gray-700 rounded-full">
              <Camera className="h-8 w-8 text-gray-400" />
            </div>
          </div>

          <div>
            <p className="text-lg font-medium text-gray-900 dark:text-white">
              {isUploading ? 'Subiendo fotos...' : 'Selecciona tus fotos'}
            </p>
            <p className="text-gray-600 dark:text-gray-400">
              Arrastra y suelta o haz clic para seleccionar
            </p>
          </div>

          <div className="text-sm text-gray-500 dark:text-gray-400">
            <p>JPG, PNG, GIF hasta 10MB</p>
            <p>Máximo {maxPhotos} fotos</p>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center">
          <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
          <p className="text-red-700 dark:text-red-400 text-sm">{error}</p>
        </div>
      )}

      {/* Upload Progress */}
      {isUploading && uploadProgress.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-gray-900 dark:text-white">Progreso de subida:</h4>
          {uploadProgress.map((progress) => (
            <div key={progress.photoIndex} className="flex items-center space-x-3">
              <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div 
                  className="bg-green-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progress.progress}%` }}
                />
              </div>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Foto {progress.photoIndex + 1}
              </span>
              {progress.status === 'complete' && (
                <CheckCircle className="h-4 w-4 text-green-500" />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Photo Previews */}
      {showPreview && previews.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-gray-900 dark:text-white">
              Fotos seleccionadas ({selectedFiles.length}/{maxPhotos})
            </h4>
            {selectedFiles.length > 0 && !isUploading && (
              <button
                onClick={handleUpload}
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors duration-200"
              >
                Subir Fotos
              </button>
            )}
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {previews.map((preview, index) => (
              <div key={index} className="relative group">
                <div className="aspect-square rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700">
                  <img
                    src={preview}
                    alt={`Preview ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </div>
                {!isUploading && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFile(index);
                    }}
                    className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors duration-200 opacity-0 group-hover:opacity-100"
                  >
                    <X size={16} />
                  </button>
                )}
                <div className="absolute bottom-2 left-2 right-2">
                  <div className="bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded">
                    {(selectedFiles[index].size / 1024 / 1024).toFixed(1)}MB
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotoUploadWidget;